import type { SelectOption } from '@/components/ui'

/**
 * Every module the backend writes audit entries under. Mirrors portal-be
 * `AUDIT_MODULES` (src/modules/audit-logs/constants/audit.enums.ts): the FE
 * can't import from the backend repo and the API exposes no module list, so
 * this is the single FE copy. The list endpoint rejects any other `module`
 * value with a 400. auditModules.test.ts pins the full list; update both
 * when the backend adds a module.
 */
export const AUDIT_MODULES = [
  'auth',
  'users',
  'roles',
  'organization',
  'companies',
  'attendance',
  'shifts',
  'leave',
  'payroll',
  'documents',
  'penalties',
  'bonuses',
  'expenses',
  'security_ledger',
  'settings',
  'announcements',
  'tax_rules',
  'hr',
] as const

export type AuditModule = (typeof AUDIT_MODULES)[number]

const AUDIT_MODULE_LABELS: Record<AuditModule, string> = {
  auth: 'Auth',
  users: 'Users',
  roles: 'Roles',
  organization: 'Organization',
  companies: 'Companies',
  attendance: 'Attendance',
  shifts: 'Shifts',
  leave: 'Leave',
  payroll: 'Payroll',
  documents: 'Documents',
  penalties: 'Penalties',
  bonuses: 'Bonuses',
  expenses: 'Expenses',
  security_ledger: 'Security ledger',
  settings: 'Settings',
  announcements: 'Announcements',
  tax_rules: 'Tax rules',
  hr: 'HR',
}

function isAuditModule(value: string): value is AuditModule {
  return (AUDIT_MODULES as readonly string[]).includes(value)
}

/** Display label for a module value; unknown values are humanized. */
export function auditModuleLabel(module: string): string {
  if (isAuditModule(module)) return AUDIT_MODULE_LABELS[module]
  const words = module.replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

export const AUDIT_MODULE_OPTIONS: SelectOption[] = [
  { value: '', label: 'All modules' },
  ...AUDIT_MODULES.map((m) => ({ value: m, label: AUDIT_MODULE_LABELS[m] })),
]
